import { isObject } from "common/utils/core";

const APP_MONITORING_KEY = "__APP_MONITORING__";

type NotifyOptions = {
  context?: Record<string, unknown>;
  params?: Record<string, unknown>;
  tags?: string;
  [key: string]: unknown;
};

const getFlags = (): { datadogEnabled: boolean; honeybadgerEnabled: boolean } => {
  const flags = (window as unknown as Record<string, unknown>)[APP_MONITORING_KEY] as
    | { flags?: { datadogEnabled?: boolean; honeybadgerEnabled?: boolean } }
    | undefined;
  return {
    datadogEnabled: flags?.flags?.datadogEnabled !== false,
    honeybadgerEnabled: flags?.flags?.honeybadgerEnabled === true
  };
};

const resolveContext = (options: NotifyOptions | undefined): Record<string, unknown> => {
  if (!options || !isObject(options)) return {};
  if (isObject(options.context)) {
    const { context, params, ...rest } = options;
    return {
      ...(context as Record<string, unknown>),
      ...(isObject(params) ? (params as Record<string, unknown>) : {}),
      ...rest
    };
  }
  return options as Record<string, unknown>;
};

const mergeGlobalContext = (callSiteContext: Record<string, unknown>): Record<string, unknown> => {
  const appMonitoring = (window as unknown as Record<string, unknown>)[APP_MONITORING_KEY] as
    | { context?: Record<string, unknown>; user?: Record<string, unknown> }
    | undefined;
  const globalContext = appMonitoring?.context || {};
  const globalUser = appMonitoring?.user || {};
  return { ...globalContext, ...globalUser, ...callSiteContext };
};

const reportToDatadog = (messageOrError: string | Error, options?: NotifyOptions): void => {
  try {
    if (!window.DD_RUM || typeof window.DD_RUM.addError !== "function") return;

    const callSiteContext = resolveContext(options);
    const mergedContext = mergeGlobalContext(callSiteContext);

    if (messageOrError instanceof Error) {
      window.DD_RUM.addError(messageOrError, mergedContext);
    } else {
      const error = new Error(messageOrError);
      error.name = "CustomError";
      window.DD_RUM.addError(error, mergedContext);
    }
  } catch (_e) {
    // Observability must never crash the app.
  }
};

const reportToHoneybadger = (messageOrError: string | Error, options?: NotifyOptions): void => {
  try {
    const hb = (window as unknown as Record<string, unknown>)["Honeybadger"] as
      | { notify?: (msg: string | Error, opts?: Record<string, unknown>) => void }
      | undefined;
    if (hb && typeof hb.notify === "function") {
      hb.notify(messageOrError, options as Record<string, unknown>);
    }
  } catch (_e) {
    // Observability must never crash the app.
  }
};

export function notifyError(messageOrError: string | Error, options?: NotifyOptions): void {
  const { datadogEnabled, honeybadgerEnabled } = getFlags();

  if (datadogEnabled) {
    reportToDatadog(messageOrError, options);
  }

  if (honeybadgerEnabled) {
    reportToHoneybadger(messageOrError, options);
  }
}

// Expose notifyError for legacy Sprockets-bundled scripts that cannot use ES module imports.
// These scripts access it via window.__APP_MONITORING__.notifyError?.().
if (!(window as unknown as Record<string, unknown>)[APP_MONITORING_KEY]) {
  (window as unknown as Record<string, unknown>)[APP_MONITORING_KEY] = {};
}
((window as unknown as Record<string, unknown>)[APP_MONITORING_KEY] as Record<string, unknown>)[
  "notifyError"
] = notifyError;
